import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import { SITE_NAME } from '@/mocks/site';
import { useState, useEffect } from 'react';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [showAllChapters, setShowAllChapters] = useState(false);

  useEffect(() => {
    setSelectedChapter(null);
    setShowAllChapters(false);
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 pt-16">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">你查找的漫画可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic, 6);
  const categoryLabel: Record<string, string> = {
    romance: '恋爱甜宠',
    fantasy: '奇幻冒险',
    action: '热血动作',
    mystery: '悬疑推理',
    comedy: '搞笑日常',
    harem: '后宫恋爱',
    drama: '剧情深度',
  };

  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 10);
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return `${v}`;
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${SITE_NAME}`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate || comic.updateDate,
    dateModified: comic.updateDate,
    publisher: { '@type': 'Organization', name: SITE_NAME },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50 pt-16">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel[comic.category] || comic.category, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Detail Header */}
          <article className="flex flex-col md:flex-row gap-6 md:gap-10 mb-10">
            {/* Cover */}
            <div className="flex-shrink-0 w-full md:w-[280px]">
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium text-background-50 ${
                    comic.status === '连载中' ? 'bg-primary-500' : 'bg-secondary-500'
                  }`}>
                    {comic.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-sm">
                  <span className="text-foreground-500">作者：</span>
                  <span className="text-foreground-800 font-medium">{comic.author}</span>
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1 text-sm">
                  <span className="text-foreground-500">分类：</span>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-primary-500 hover:text-primary-600 font-medium"
                  >
                    {categoryLabel[comic.category]}
                  </Link>
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1 text-sm">
                  <span className="text-foreground-500">状态：</span>
                  <span className="text-foreground-800 font-medium">{comic.status}</span>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="flex items-center gap-1 text-sm">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-semibold text-foreground-900">{comic.rating}</span>
                  <span className="text-foreground-500">评分</span>
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {formatViews(comic.views)} 阅读
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-file-list-3-line"></i>
                  共 {comic.chapters.length} 话
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                {comic.description}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    if (comic.chapters.length > 0) {
                      setSelectedChapter(comic.chapters[comic.chapters.length - 1].id);
                      setTimeout(() => {
                        document.getElementById('chapters-section')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  开始阅读
                </button>
                <button
                  onClick={() => {
                    document.getElementById('chapters-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看目录
                </button>
              </div>
            </div>
          </article>

          {/* Chapters Section */}
          <section id="chapters-section" className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-foreground-900 font-heading">章节列表</h2>
                <p className="text-xs text-foreground-500 mt-1">共 {comic.chapters.length} 话 · 最近更新：{comic.updateDate}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAllChapters(true)}
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${
                    showAllChapters
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  正序
                </button>
              </div>
            </div>

            {/* Selected Chapter Viewer */}
            {selectedChapter && (
              <div className="mb-6 p-6 bg-background-100 rounded-lg border border-background-200/70">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground-800">
                    正在阅读：第{selectedChapter}话
                  </h3>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const idx = comic.chapters.findIndex((c) => c.id === selectedChapter);
                        if (idx > 0) setSelectedChapter(comic.chapters[idx - 1].id);
                      }}
                      disabled={selectedChapter <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-up-s-line"></i>
                    </button>
                    <span className="text-xs text-foreground-500">{selectedChapter}/{comic.chapters.length}</span>
                    <button
                      onClick={() => {
                        const idx = comic.chapters.findIndex((c) => c.id === selectedChapter);
                        if (idx < comic.chapters.length - 1) setSelectedChapter(comic.chapters[idx + 1].id);
                      }}
                      disabled={selectedChapter >= comic.chapters.length}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-down-s-line"></i>
                    </button>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg p-8 min-h-[400px] flex flex-col items-center justify-center border border-dashed border-background-300/60">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className="ri-book-open-line text-3xl text-primary-500"></i>
                  </div>
                  <p className="text-foreground-700 font-medium mb-2">第{selectedChapter}话 内容区域</p>
                  <p className="text-xs text-foreground-500 text-center max-w-md">
                    此处将展示完整的漫画章节内容。在正式环境中，这里将加载下拉式韩漫的完整图片内容。
                  </p>
                  <div className="flex items-center gap-4 mt-6">
                    <button
                      onClick={() => {
                        const idx = comic.chapters.findIndex((c) => c.id === selectedChapter);
                        if (idx > 0) setSelectedChapter(comic.chapters[idx - 1].id);
                      }}
                      disabled={selectedChapter <= 1}
                      className="px-4 py-2 rounded-full bg-background-200 text-foreground-700 text-sm hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-s-line mr-1"></i>上一话
                    </button>
                    <button
                      onClick={() => {
                        const idx = comic.chapters.findIndex((c) => c.id === selectedChapter);
                        if (idx < comic.chapters.length - 1) setSelectedChapter(comic.chapters[idx + 1].id);
                      }}
                      disabled={selectedChapter >= comic.chapters.length}
                      className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      下一话<i className="ri-arrow-right-s-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Chapter List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {displayedChapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => setSelectedChapter(chapter.id)}
                  className={`text-left px-4 py-3 rounded-lg border transition-all text-sm cursor-pointer ${
                    selectedChapter === chapter.id
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-background-50 border-background-200/70 text-foreground-700 hover:border-primary-200 hover:bg-primary-50/50'
                  }`}
                >
                  <span className="font-medium block truncate">{chapter.title}</span>
                  <span className="text-xs text-foreground-400 mt-0.5 block">{chapter.updateDate}</span>
                </button>
              ))}
            </div>
            {!showAllChapters && comic.chapters.length > 10 && (
              <button
                onClick={() => setShowAllChapters(true)}
                className="mt-4 w-full py-3 rounded-lg bg-background-100 border border-background-200/70 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer"
              >
                查看全部 {comic.chapters.length} 话 <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
            )}
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="pb-12">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">相关推荐</h2>
                <p className="text-xs text-foreground-500 mt-1">喜欢这部漫画的人也看了</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} showCategory />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}